import { useState, useRef, useEffect, useCallback } from 'react'
import { Link, router } from '@inertiajs/react'
import { Bell, Check, CheckCheck, AtSign, MessageCircle, Users, ExternalLink } from 'lucide-react'
import type { NotificationItem } from '@/types'

interface NotificationDropdownProps {
  unreadCount: number
}

function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) return "à l'instant"
  if (diffInSeconds < 3600) return `il y a ${Math.floor(diffInSeconds / 60)} min`
  if (diffInSeconds < 86400) return `il y a ${Math.floor(diffInSeconds / 3600)} h`
  if (diffInSeconds < 604800) return `il y a ${Math.floor(diffInSeconds / 86400)} j`

  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
}

function getNotificationIcon(type: string) {
  switch (type) {
    case 'mention': return <AtSign className="w-4 h-4 text-blue-500" />
    case 'new_comment': return <MessageCircle className="w-4 h-4 text-green-500" />
    case 'new_reply': return <MessageCircle className="w-4 h-4 text-tessan-primary" />
    case 'subscription_update': return <Users className="w-4 h-4 text-purple-500" />
    default: return <Bell className="w-4 h-4 text-gray-400" />
  }
}

export function NotificationDropdown({ unreadCount }: NotificationDropdownProps) {
  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState<NotificationItem[]>([])
  const [loading, setLoading] = useState(false)
  const [localUnreadCount, setLocalUnreadCount] = useState(unreadCount)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Sync with props
  useEffect(() => {
    setLocalUnreadCount(unreadCount)
  }, [unreadCount])

  const fetchNotifications = useCallback(async () => {
    setLoading(true)
    try {
      const response = await fetch('/notifications/recent', {
        headers: { 'Accept': 'application/json' }
      })
      if (response.ok) {
        const data = await response.json()
        setNotifications(data.notifications)
        setLocalUnreadCount(data.unread_count)
      }
    } catch {
      // ignore
    } finally {
      setLoading(false)
    }
  }, [])

  const handleToggle = () => {
    if (!open) {
      fetchNotifications()
    }
    setOpen(!open)
  }

  const handleMarkAsRead = (id: number) => {
    router.post(`/notifications/${id}/mark_as_read`, {}, {
      preserveScroll: true,
      onSuccess: () => {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))
        setLocalUnreadCount(prev => Math.max(0, prev - 1))
      }
    })
  }

  const handleMarkAllAsRead = () => {
    router.post('/notifications/mark_all_as_read', {}, {
      preserveScroll: true,
      onSuccess: () => {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })))
        setLocalUnreadCount(0)
      }
    })
  }

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={handleToggle}
        className="relative p-2 text-text-secondary hover:bg-gray-100 rounded-lg transition-colors"
        title="Notifications"
      >
        <Bell size={20} />
        {localUnreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full">
            {localUnreadCount > 99 ? '99+' : localUnreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="font-heading font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center gap-2">
              {localUnreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="inline-flex items-center gap-1 text-xs text-tessan-primary hover:bg-tessan-primary/10 px-2 py-1 rounded transition-colors"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  Tout lire
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="max-h-[400px] overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-5 h-5 border-2 border-tessan-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8">
                <Bell className="w-10 h-10 mx-auto mb-2 text-gray-200" />
                <p className="text-sm text-gray-500">Aucune notification</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-gray-50 last:border-0 transition-colors ${
                    !notification.read ? 'bg-blue-50/60' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    {getNotificationIcon(notification.type)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <Link
                      href={notification.url}
                      onClick={() => {
                        if (!notification.read) handleMarkAsRead(notification.id)
                        setOpen(false)
                      }}
                      className="block"
                    >
                      <p className={`text-sm leading-snug ${!notification.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                        {notification.message}
                      </p>
                      {notification.data?.text_preview && (
                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                          "{notification.data.text_preview}"
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {formatRelativeDate(notification.created_at)}
                      </p>
                    </Link>
                  </div>

                  {!notification.read && (
                    <button
                      onClick={() => handleMarkAsRead(notification.id)}
                      className="flex-shrink-0 p-1 text-gray-400 hover:text-tessan-primary hover:bg-gray-100 rounded transition-colors"
                      title="Marquer comme lu"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100">
            <Link
              href="/notifications"
              onClick={() => setOpen(false)}
              className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm text-tessan-primary hover:bg-gray-50 transition-colors"
            >
              Voir toutes les notifications
              <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
